import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { sidebarContent } from "@/components/RComponents/SidebarComponents/data/sidebarContent";
import RAppSidebar from "@/components/RComponents/SidebarComponents/RAppSidebar";
import { createContext, useEffect, useState } from "react";
import RNavbar from "@/components/RComponents/RNavbar";
import { Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import RProgressBar from "@/components/RComponents/RProgressBar/RProgressBar";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";

// Define the shape of the theme context
export interface DashboardContextType {
  theme: string;
  toggleTheme: (newTheme: string) => void;
  activeLanguage: string;
  switchLanguage: (language: string) => void;
}

// Create the ThemeContext with a default value of `undefined` initially
// eslint-disable-next-line react-refresh/only-export-components
export const DashboardContext = createContext<DashboardContextType>({
  theme: "",
  toggleTheme: () => { },
  activeLanguage: "en",
  switchLanguage: () => { },
});

export default function DashboardLayout() {
  const { user } = useSelector((state: RootState) => state.auth);

  const [theme, setTheme] = useState<string>(
    localStorage.getItem("theme") || "main-theme-light"
  );
  const [activeLanguage, setActiveLanguage] = useState<string>("en");
  const { i18n } = useTranslation();
  
  useEffect(() => {
    // Apply saved theme from localStorage or use default
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme) {
      setTheme(savedTheme);
      document.querySelector("html")?.setAttribute("data-theme", savedTheme);
    }
    const language = localStorage.getItem("lang");
    if (language) {
      setActiveLanguage(language);
      i18n.changeLanguage(language);
      // Set initial direction based on saved language
      document.querySelector("html")?.setAttribute("dir", language === "ar" ? "rtl" : "ltr");
    }
    else document.querySelector("html")?.setAttribute("dir", "ltr")
  }, [i18n]);


  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (!savedTheme)
      localStorage.setItem("theme", "main-theme-light")
    const savedLang = localStorage.getItem("lang");
    if (!savedLang)
      localStorage.setItem("lang", "en")
  }, [])

  // Define the toggleTheme function with its type
  const toggleTheme = (newTheme: string) => {
    setTheme(newTheme);
    document.querySelector("html")?.setAttribute("data-theme", newTheme);
    localStorage.setItem("theme", newTheme); // Persist theme
  };


  const switchLanguage = (language: string) => {
    i18n.changeLanguage(language);
    setActiveLanguage(language);
    document.querySelector("html")?.setAttribute("lang", language);
    document.querySelector("html")?.setAttribute("dir", language === "ar" ? "rtl" : "ltr");
    localStorage.setItem("lang", language);
    window.location.reload();
  };
  // const [isResetOpen, setIsResetOpen] = useState<boolean>(false);

  return (
    <SidebarProvider>
      <DashboardContext.Provider
        value={{ theme, toggleTheme, activeLanguage, switchLanguage }}
      >
        <RProgressBar />
        <RAppSidebar contentData={sidebarContent} user={user}/>
        <SidebarInset>
          <RNavbar />
          <div id="content wrapper" className="p-5 z-[1]">
            <Outlet />
          </div>
        </SidebarInset>
      </DashboardContext.Provider>
    </SidebarProvider>
  );
}
